import React from 'react';
import { Link } from 'react-router-dom';
import {
  ChartPieIcon,
  PencilSquareIcon,
  ShoppingCartIcon,
  WrenchScrewdriverIcon,
  TruckIcon,
  BuildingOffice2Icon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';

// The seven interoperable Mantle modules. Each client starts with the base
// modules and activates more as needed. Cards link straight to the section.
// TODO: Map the modules in the list below to modules as specified in `md_modules`. Use a filter to only show enabled modules.
const MODULES = [
  {
    code: 'MOD_1_OPEX',
    name: 'Site-Level OPEX',
    path: '/opex',
    icon: PencilSquareIcon,
    blurb: 'The foundation. Driver-based budgets built on "Price × Quantity" rather than generic GL averages.',
  },
  {
    code: 'MOD_2_SUPPLY',
    name: 'Supply Chain',
    path: '/purchase',
    icon: ShoppingCartIcon,
    blurb: 'Input costing — the true landed cost of getting raw materials and stock to each site.',
  },
  {
    code: 'MOD_3_MAKE',
    name: 'Production',
    path: '/manufacturing',
    icon: WrenchScrewdriverIcon,
    blurb: 'The "Make" module. Bills of materials, direct labour and yield loss as inputs become outputs.',
  },
  {
    code: 'MOD_4_MOVE',
    name: 'Distribution',
    path: '/distribution',
    icon: TruckIcon,
    blurb: 'The "Move" module. Warehousing, distribution centres, forklifts and picking crews.',
  },
  {
    code: 'MOD_5_CAPEX',
    name: 'Capital',
    path: '/capital',
    icon: BuildingOffice2Icon,
    blurb: 'The growth module. Budget new projects and asset acquisition, linked to operational cash flow.',
  },
  {
    code: 'MOD_6_PROFIT', // Is this correct?
    name: 'Revenue',
    path: '/revenue',
    icon: BanknotesIcon,
    blurb: 'The strategic engine. "Implodes" every active cost into a bottom-up break-even price.',
  },
  {
    code: 'MOD_10_REPORT',
    name: 'Reports',
    path: '/reports',
    icon: ChartPieIcon,
    blurb: 'Enterprise visibility. Granular site-level reporting that rolls up to country and group level.',
  },
];

const Welcome = () => (
  <div className="h-full overflow-y-auto font-sans bg-slate-100 dark:bg-slate-900">
    <div className="max-w-5xl mx-auto px-6 py-10">

      {/* Hero */}
      <div className="rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 px-8 py-10 text-white shadow-sm">
        <p className="text-sm font-semibold uppercase tracking-[2px] text-slate-400">
          Mantle &middot; Budgeting &amp; Forecasting
        </p>
        <h1 className="mt-3 text-4xl font-bold tracking-tight">Welcome to Mantle</h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-300">
          Mantle is the strategic planning layer that sits on top of your ERP — turning static
          master data into dynamic, driver-based financial plans. Build a precise
          &ldquo;Price&nbsp;&times;&nbsp;Quantity&rdquo; budget from the smallest operational atom and
          roll it all the way up to the C-suite.
        </p>
      </div>

      {/* Modules */}
      <div className="mt-10">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Your modules</h2>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          A composable stack — activate only the operational blocks your business needs.
        </p>

        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {/* TODO: GET a list of active modules from DB and match `code` in MODULES to `module_code` in `md_modules` table, filtering out any that are inactive. */}
          {MODULES.map(({ name, path, icon: Icon, blurb }) => (
            <Link
              key={name}
              to={path}
              className="group flex flex-col rounded-xl border border-slate-200 bg-white p-5 transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md dark:border-slate-700 dark:bg-slate-800 dark:hover:border-slate-600"
            >
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100 text-slate-700 group-hover:bg-slate-900 group-hover:text-white dark:bg-slate-700 dark:text-slate-200">
                <Icon className="h-5 w-5" />
              </span>
              <h3 className="mt-4 text-base font-semibold text-slate-900 dark:text-slate-100">{name}</h3>
              <p className="mt-1 text-sm leading-relaxed text-slate-500 dark:text-slate-400">{blurb}</p>
            </Link>
          ))}
        </div>
      </div>

    </div>
  </div>
);

export default Welcome;
